<script setup>
/** 一场分镜的三段因果：来源回合 → 改编写了什么 → 供应商生成了什么。 */
import { computed } from 'vue'
import { Badge } from '@drama/ui'
import { useI18n } from 'vue-i18n'

import { formatDate, formatSize } from '../generation-records-format.js'
import BeatText from './BeatText.vue'
import SceneCalls from './SceneCalls.vue'
import { segmentStage, stageVariant } from './segment-stage.js'

const props = defineProps({
  scene: { type: Object, required: true },
  segment: { type: Object, default: null },
  sourceEntries: { type: Array, required: true },
  /** 续接的上一场：`{ sceneNo, endFrameUrl }`，首场为 null。 */
  chainFrom: { type: Object, default: null },
})

const { t } = useI18n()

const stage = computed(() => segmentStage(props.segment))

const speechMarks = computed(() => (props.scene.speech ?? []).map(line => (
  `${line.start}s ${t(`visualGameAdmin.generationRecords.process.transcript.${line.speaker === 'npc' ? 'npc' : 'player'}`)}`
)))

const renderParams = computed(() => {
  const h3 = props.scene.h3
  if (!h3) return []
  return [
    `${h3.width}×${h3.height}`,
    t('visualGameAdmin.generationRecords.process.scene.frames', { count: h3.frames }),
    `steps ${h3.steps}`,
    `seed ${h3.seed}`,
    h3.turbo ? 'turbo' : null,
  ].filter(param => param !== null)
})

const promptLength = computed(() => t('visualGameAdmin.generationRecords.process.scene.promptChars', { count: [...(props.scene.prompt ?? '')].length }))
</script>

<template>
  <div v-if="chainFrom" class="scene-card__chain">
    <img v-if="chainFrom.endFrameUrl" :src="chainFrom.endFrameUrl" :alt="t('visualGameAdmin.generationRecords.process.scene.endFrame')" />
    <span>
      {{ scene.chain === 'extend'
        ? t('visualGameAdmin.generationRecords.process.scene.chainFrom', { scene: chainFrom.sceneNo })
        : t('visualGameAdmin.generationRecords.process.scene.anchor') }}
    </span>
  </div>

  <article :id="`scene-${scene.scene_no}`" class="scene-card">
    <header class="scene-card__header">
      <strong>{{ t('visualGameAdmin.generationRecords.process.scene.scene', { scene: scene.scene_no }) }}</strong>
      <Badge variant="neutral" size="sm">{{ t(`visualGameAdmin.generationRecords.process.scene.${scene.chain}`) }}</Badge>
      <span>{{ t('visualGameAdmin.generationRecords.process.scene.turn', { turn: scene.turn_no }) }}</span>
      <span v-if="scene.title">· {{ scene.title }}</span>
      <span>· {{ scene.duration }}s</span>
      <Badge :variant="stageVariant(stage)" size="sm" class="scene-card__stage">
        {{ t(`visualGameAdmin.generationRecords.process.scene.stage.${stage}`) }}
      </Badge>
    </header>

    <div class="scene-card__grid">
      <div class="scene-card__col">
        <h3>{{ t('visualGameAdmin.generationRecords.process.scene.source') }}</h3>
        <BeatText v-for="(entry, index) in sourceEntries" :key="index" :entry="entry" />
        <p v-if="segment?.semantic_text" class="scene-card__semantic">{{ segment.semantic_text }}</p>
      </div>

      <div class="scene-card__col">
        <h3>{{ t('visualGameAdmin.generationRecords.process.scene.screenplay') }}</h3>
        <p v-if="scene.narrative" class="scene-card__narrative">{{ scene.narrative }}</p>
        <ul v-if="speechMarks.length > 0" class="scene-card__chips">
          <li v-for="mark in speechMarks" :key="mark">{{ mark }}</li>
        </ul>
        <ul v-if="renderParams.length > 0" class="scene-card__chips is-mono">
          <li v-for="param in renderParams" :key="param">{{ param }}</li>
        </ul>
        <details class="scene-card__prompt">
          <summary>{{ t('visualGameAdmin.generationRecords.process.scene.prompt') }} · {{ promptLength }}</summary>
          <pre>{{ scene.prompt }}</pre>
        </details>
      </div>

      <div class="scene-card__col">
        <h3>{{ t('visualGameAdmin.generationRecords.process.scene.production') }}</h3>
        <video v-if="segment?.clip" :src="segment.clip.public_url" controls preload="metadata" class="scene-card__clip" />
        <div v-else class="scene-card__clip is-placeholder">{{ t(`visualGameAdmin.generationRecords.process.scene.stage.${stage}`) }}</div>
        <div v-if="segment?.first_frame_url || segment?.end_frame_url" class="scene-card__frames">
          <figure v-if="segment.first_frame_url">
            <img :src="segment.first_frame_url" :alt="t('visualGameAdmin.generationRecords.process.scene.firstFrame')" />
            <figcaption>{{ t('visualGameAdmin.generationRecords.process.scene.firstFrame') }}</figcaption>
          </figure>
          <figure v-if="segment.end_frame_url">
            <img :src="segment.end_frame_url" :alt="t('visualGameAdmin.generationRecords.process.scene.endFrame')" />
            <figcaption>{{ t('visualGameAdmin.generationRecords.process.scene.endFrame') }}</figcaption>
          </figure>
        </div>
        <p v-if="segment?.resource" class="scene-card__resource">
          <Badge variant="neutral" size="sm">{{ segment.resource.status }}</Badge>
          <code>{{ segment.resource.id }}</code>
          <span>{{ formatDate(segment.resource.created_at) }}</span>
          <span v-if="segment.resource.reviewed_by">· {{ segment.resource.reviewed_by }} · {{ formatDate(segment.resource.reviewed_at) }}</span>
          <span v-if="segment.resource.rejection_reason" class="scene-card__rejection">{{ segment.resource.rejection_reason }}</span>
        </p>
        <p v-if="segment?.clip" class="scene-card__hash">{{ formatSize(segment.clip.size) }} · {{ segment.clip.hash }}</p>

        <h4>{{ t('visualGameAdmin.generationRecords.process.scene.calls') }}</h4>
        <SceneCalls :calls="segment?.calls ?? []" />
      </div>
    </div>
  </article>
</template>

<style scoped>
.scene-card__chain {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  margin-left: var(--drama-space-xl);
  padding: var(--drama-space-md) 0 var(--drama-space-md) var(--drama-space-lg);
  border-left: 2px dashed var(--drama-color-border-strong);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.scene-card__chain img {
  width: 96px;
  border-radius: var(--drama-radius-sm);
}

.scene-card {
  overflow: hidden;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  scroll-margin-top: var(--drama-space-xl);
}

.scene-card:target {
  border-color: var(--drama-color-info);
}

.scene-card__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-md) var(--drama-space-lg);
  background: var(--drama-color-surface-alt);
  font-size: var(--drama-font-size-sm);
}

.scene-card__header span {
  color: var(--drama-color-text-muted);
}

.scene-card__stage {
  margin-left: auto;
}

.scene-card__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.scene-card__col {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-sm);
  min-width: 0;
  padding: var(--drama-space-lg);
  font-size: var(--drama-font-size-sm);
}

.scene-card__col + .scene-card__col {
  border-left: 1px solid var(--drama-color-border);
}

.scene-card__col h3,
.scene-card__col h4 {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  font-weight: var(--drama-font-weight-medium);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.scene-card__col h4 {
  margin-top: var(--drama-space-sm);
}

.scene-card__col p {
  margin: 0;
  line-height: 1.6;
  word-break: break-word;
}

.scene-card__semantic {
  padding-top: var(--drama-space-sm);
  border-top: 1px dashed var(--drama-color-border);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.scene-card__narrative {
  white-space: pre-wrap;
}

.scene-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-xs);
  margin: 0;
  padding: 0;
  list-style: none;
}

.scene-card__chips li {
  padding: 0 var(--drama-space-sm);
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  line-height: 20px;
}

.scene-card__chips.is-mono li {
  font-family: var(--drama-font-family-mono);
}

.scene-card__prompt summary {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  cursor: pointer;
}

.scene-card__prompt pre {
  margin: var(--drama-space-sm) 0 0;
  padding: var(--drama-space-md);
  overflow: auto;
  max-height: 480px;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-bg);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
  white-space: pre-wrap;
}

.scene-card__clip {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-bg);
}

.scene-card__clip.is-placeholder {
  display: grid;
  place-items: center;
  border: 1px dashed var(--drama-color-border-strong);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.scene-card__frames {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--drama-space-sm);
}

.scene-card__frames figure {
  margin: 0;
}

.scene-card__frames img {
  width: 100%;
  border-radius: var(--drama-radius-sm);
}

.scene-card__frames figcaption {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.scene-card__resource {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.scene-card__resource code,
.scene-card__hash {
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
}

.scene-card__rejection {
  color: var(--drama-color-danger);
}

.scene-card__hash {
  color: var(--drama-color-text-muted);
  word-break: break-all;
}

@media (max-width: 960px) {
  .scene-card__grid {
    grid-template-columns: 1fr;
  }

  .scene-card__col + .scene-card__col {
    border-top: 1px solid var(--drama-color-border);
    border-left: none;
  }
}
</style>
